// Telling "one loop, several renders" from "two different sounds", cheaply.
//
// `lib/material.ts` groups clips by their DESCRIPTION because the audio object
// over-splits: Live fades a clip's edges, the fade depends on what the clip
// abuts, and the same four bars placed five times comes back as several
// content-addressed objects. But the description under-splits too — two clips
// can share a name, a window and a length and still be different sounds (a
// transposed duplicate, a MIDI lane re-bounced after an edit), and merging
// those loses one of them from the shelf entirely.
//
// So where the description says "same" and the objects say "different", ask
// the audio. The question is RELATIVE — "is this object the same piece as that
// one" — and this module answers it that way, by comparing an interior window
// of one object against another's. It used to answer an absolute one instead,
// hashing each object's window on its own, and that was wrong in a way no
// amount of care over the window could fix:
//
//   On `s_jazzy_mukjbbry` (2026-09-27) one track's sixteen placements of a
//   drum loop are backed by six objects. All six are the SAME audio — their
//   interiors are byte-identical — but they sit at different offsets and
//   lengths: Live starts some renders one 24-bit stereo frame (6 bytes) later
//   than others, and their totals are 977030 or 977024, a frame apart. A hash
//   of a fixed byte offset therefore read a DIFFERENT window out of each
//   shifted file, and the total went into the hash besides. Six objects, three
//   answers, three cards and three pads for one loop — from the very probe
//   that exists to prevent exactly that.
//
// Hence: fetch one interior window per object and compare windows, allowing a
// small alignment shift. A byte-exact match over 16KB at some shift is proof
// of the same take; nothing else comes close to one.
//
// Cost is unchanged: one small Range request per distinct object, cached
// forever (the keys are content hashes, so an object can never change under
// its key), and only for objects that actually collide. A Set whose clips all
// describe themselves differently makes no requests at all.

/** The stretch that has to match, byte for byte, for two objects to be one
 * piece. Two different takes diverge everywhere — over 16KB they differ in
 * roughly half their bytes — so this is far more than enough to see it. */
const CORE_BYTES = 16 * 1024;

/**
 * How far one render may sit from another and still be the same piece.
 *
 * ~15ms of 44.1kHz 24-bit stereo. The shifts actually measured are a single
 * frame; this is generous by three orders of magnitude and still nowhere near
 * any musical period, so a loop can never align with itself a beat over.
 */
const SLACK_BYTES = 4 * 1024;

/** What each window request pulls: the core, with slack to slide it in. */
const SPAN_BYTES = CORE_BYTES + 2 * SLACK_BYTES;

/** Where to read it from. Away from both edges, which is the whole point —
 * a head or tail window would compare exactly the fades that differ. */
const WINDOW_AT = 0.45;

/** The anchor used to find the alignment before verifying it. Long enough
 * that a chance hit is not a thing that happens. */
const NEEDLE_BYTES = 64;

/** A needle has to carry this much variation to be worth searching for —
 * silence and flat runs match everywhere and would cost a verify each. */
const NEEDLE_VALUES = 4;

/** Backstop on full verifications per comparison, for pathological audio
 * where even a varied needle recurs. A real alignment is found on the first. */
const MAX_VERIFIES = 32;

/** An interior window of one object, and where in the object it came from. */
export interface AudioWindow {
  /** The object's full size, as the probe's Content-Range reported it. */
  total: number;
  /** Byte offset `bytes` was read from. */
  at: number;
  bytes: Uint8Array;
}

const cache = new Map<string, Promise<AudioWindow | null>>();

/**
 * One object's interior window, by its cache key (the content hash is ideal —
 * it is stable and unique per object). Concurrent callers share one request;
 * the result is kept for the life of the page.
 *
 * Null means "could not tell", and the caller then leaves the clips MERGED on
 * their description. That is deliberate: the cheap check is the only thing
 * that makes splitting affordable, and without it the alternative is not
 * "split safely" but "download a megabyte per object to find out" — which on a
 * phone is worse than the rare wrong merge it would prevent. Merged is also
 * exactly what the shelf did before any of this, so a server without Range
 * simply gets the old behaviour.
 */
export function audioWindow(cacheKey: string, url: string, fetcher = fetch): Promise<AudioWindow | null> {
  const hit = cache.get(cacheKey);
  if (hit) return hit;
  const pending = read(url, fetcher).catch(() => null);
  cache.set(cacheKey, pending);
  return pending;
}

async function read(url: string, fetcher: typeof fetch): Promise<AudioWindow | null> {
  // One probe settles both questions: whether this server does ranges at all,
  // and how big the object is (a 206 reports the total in Content-Range, so
  // there is no separate HEAD).
  //
  // A server that ignores Range answers 200 and starts sending the WHOLE
  // object. `fetch` resolves as soon as the headers land, so the status is
  // known before the body matters — abort there and the megabyte never
  // arrives. Reading it to find out would cost exactly what this function
  // exists to avoid (measured against the un-deployed API: eleven probes,
  // eleven whole objects, ~10MB to learn nothing).
  const stop = new AbortController();
  const probe = await fetcher(url, { headers: { Range: "bytes=0-0" }, signal: stop.signal });
  if (probe.status !== 206) {
    stop.abort();
    return null;
  }
  const total = totalFrom(probe) ?? 0;
  await probe.arrayBuffer().catch(() => undefined); // one byte
  if (!(total > 0)) return null;

  // Objects small enough that there is no interior to sample are read whole,
  // and then the alignment search runs over all of it.
  const at =
    total <= SPAN_BYTES
      ? 0
      : Math.min(Math.max(0, Math.floor(total * WINDOW_AT) - SLACK_BYTES), total - SPAN_BYTES);
  const end = Math.min(at + SPAN_BYTES, total) - 1;
  const res = await fetcher(url, { headers: { Range: `bytes=${at}-${end}` } });
  if (!res.ok) throw new Error(`variant window: ${res.status}`);
  return { total, at, bytes: new Uint8Array(await res.arrayBuffer()) };
}

/** `bytes <start>-<end>/<total>` → total. */
function totalFrom(res: Response): number | null {
  const m = /\/(\d+)\s*$/.exec(res.headers.get("content-range") ?? "");
  return m ? Number(m[1]) : null;
}

/**
 * Are these two objects the same piece of audio?
 *
 * True when a stretch of one turns up byte for byte in the other, within the
 * shift one render may sit from another. Renders of one loop differ at their
 * faded edges and in where they start, never in the middle; two takes differ
 * in the middle and cannot be slid into agreement.
 */
export function sameAudio(a: AudioWindow, b: AudioWindow): boolean {
  // A length this far apart is a different piece outright, and says so before
  // any bytes are looked at. The real gap between renders of one loop is a
  // frame; this allows a thousand times that.
  if (Math.abs(a.total - b.total) > SLACK_BYTES) return false;
  const len = Math.min(CORE_BYTES, a.bytes.length, b.bytes.length);
  if (len === 0) return a.total === b.total;
  const from = Math.max(0, Math.min(SLACK_BYTES, a.bytes.length - len));
  // Where that stretch sits in `b` if neither render is shifted — the same
  // absolute offset in the object, read off b's own window.
  return aligns(a.bytes, from, len, b.bytes, a.at + from - b.at);
}

/** Does `ref[from … from+len)` occur in `cand` at `expected`, give or take
 * the slack? Ordered outwards from `expected`, so the smallest shift wins. */
function aligns(ref: Uint8Array, from: number, len: number, cand: Uint8Array, expected: number): boolean {
  const needle = needleIn(ref, from, len);
  // Featureless audio (a silent interior) has no anchor worth searching for;
  // compare it where it should be and leave it at that.
  if (needle === null) return equal(ref, from, cand, expected, len);
  const lead = needle - from;
  let verifies = 0;
  for (let d = 0; d <= SLACK_BYTES; d++) {
    for (const shift of d === 0 ? [0] : [d, -d]) {
      const at = expected + shift;
      if (at < 0 || at + len > cand.length) continue;
      if (!equal(ref, needle, cand, at + lead, NEEDLE_BYTES)) continue;
      if (equal(ref, from, cand, at, len)) return true;
      if (++verifies >= MAX_VERIFIES) return false;
    }
  }
  return false;
}

/** An anchor inside `ref[from … from+len)` varied enough to be worth
 * searching for, or null if the stretch has no such run. */
function needleIn(ref: Uint8Array, from: number, len: number): number | null {
  for (let i = from; i + NEEDLE_BYTES <= from + len; i++) {
    const seen = new Set<number>();
    for (let j = 0; j < NEEDLE_BYTES; j++) seen.add(ref[i + j]!);
    if (seen.size >= NEEDLE_VALUES) return i;
  }
  return null;
}

function equal(a: Uint8Array, ai: number, b: Uint8Array, bi: number, len: number): boolean {
  if (ai < 0 || bi < 0 || ai + len > a.length || bi + len > b.length) return false;
  for (let i = 0; i < len; i++) if (a[ai + i] !== b[bi + i]) return false;
  return true;
}

/**
 * The objects in question, sorted into pieces of audio.
 *
 * Returns a variant id per object: the same id for objects that are the same
 * take, so clips that share one fall back together onto one card. The id is
 * the cluster's lowest key — stable, and since the keys are content hashes it
 * names the object the card is speaking for.
 */
export function clusterVariants(windows: ReadonlyMap<string, AudioWindow>): Map<string, string> {
  const ids = new Map<string, string>();
  const clusters: Array<{ id: string; window: AudioWindow }> = [];
  // Sorted, so the id a cluster lands on is its lowest key however the
  // objects arrived — a re-probe must not rename every card.
  for (const key of [...windows.keys()].sort()) {
    const window = windows.get(key)!;
    const hit = clusters.find((c) => sameAudio(c.window, window));
    if (hit) {
      ids.set(key, hit.id);
      continue;
    }
    clusters.push({ id: key, window });
    ids.set(key, key);
  }
  return ids;
}

/** Test seam: the cache is a module singleton and specs share a module. */
export function resetWindowCache(): void {
  cache.clear();
}
